import { useEffect } from 'react';
import { useMutation, useQueryClient, UseMutationOptions, QueryKey } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Broadcast configuration for cross-device sync
 *
 * This is used for tables with complex RLS policies where postgres_changes
 * doesn't work reliably. Instead of relying on database-level realtime,
 * we emit broadcasts after successful mutations.
 */
export interface BroadcastConfig {
  /**
   * Channel name pattern. Can include {id} placeholder for dynamic channels.
   * Examples:
   * - 'calendar-groups' (static channel)
   * - 'calendar-group:{groupId}' (dynamic, will be interpolated)
   * - 'messages:{conversationId}' (dynamic)
   */
  channel: string;

  /**
   * Event name to emit
   * Examples: 'group_updated', 'member_added', 'message_sent'
   */
  event: string;

  /**
   * Function to extract channel variables from mutation variables
   * Used to interpolate {placeholders} in channel name
   */
  getChannelVars?: (variables: any) => Record<string, string>;

  /**
   * Additional payload to include in the broadcast
   * Useful for optimistic updates on receiving end
   */
  getPayload?: (data: any, variables: any) => Record<string, any>;
}

export interface UseMutationWithBroadcastOptions<TData, TError, TVariables, TContext>
  extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'onSuccess'> {
  /**
   * Query keys to invalidate after successful mutation
   * Can be static keys or functions that receive the mutation variables
   */
  invalidateKeys?: (QueryKey | ((variables: TVariables) => QueryKey))[];

  /**
   * Broadcast configuration for cross-device sync
   * Only needed for tables with complex RLS policies
   */
  broadcast?: BroadcastConfig;

  /**
   * Original onSuccess handler (will be called after invalidation and broadcast)
   */
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void | Promise<void>;
}

/**
 * Enhanced useMutation hook that automatically handles:
 * 1. React Query cache invalidation
 * 2. Supabase Broadcast emission for cross-device sync
 *
 * This solves the problem of tables with complex RLS policies where
 * postgres_changes doesn't deliver events reliably.
 *
 * @example
 * // Simple usage with static channel
 * const { mutate } = useMutationWithBroadcast({
 *   mutationFn: updateGroup,
 *   invalidateKeys: [['calendar-groups']],
 *   broadcast: {
 *     channel: 'calendar-groups',
 *     event: 'group_updated'
 *   }
 * });
 *
 * @example
 * // Dynamic channel based on mutation variables
 * const { mutate } = useMutationWithBroadcast({
 *   mutationFn: (params) => updateMember(params),
 *   invalidateKeys: [
 *     ['calendar-groups'],
 *     (vars) => ['calendar-group', vars.group_id]
 *   ],
 *   broadcast: {
 *     channel: 'calendar-group:{groupId}',
 *     event: 'member_updated',
 *     getChannelVars: (vars) => ({ groupId: vars.group_id }),
 *     getPayload: (data, vars) => ({ user_id: vars.user_id })
 *   }
 * });
 */
export function useMutationWithBroadcast<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown
>(options: UseMutationWithBroadcastOptions<TData, TError, TVariables, TContext>) {
  const queryClient = useQueryClient();

  const {
    invalidateKeys = [],
    broadcast,
    onSuccess: originalOnSuccess,
    ...mutationOptions
  } = options;

  return useMutation<TData, TError, TVariables, TContext>({
    ...mutationOptions,
    onSuccess: async (data, variables, context) => {
      // 1. Invalidate specified query keys
      const invalidationPromises = invalidateKeys.map((keyOrFn) => {
        const key = typeof keyOrFn === 'function' ? keyOrFn(variables) : keyOrFn;
        return queryClient.invalidateQueries({ queryKey: key });
      });

      await Promise.all(invalidationPromises);

      // 2. Emit broadcast for cross-device sync (if configured)
      if (broadcast) {
        try {
          // Interpolate channel name with variables
          let channelName = broadcast.channel;
          if (broadcast.getChannelVars) {
            const vars = broadcast.getChannelVars(variables);
            Object.entries(vars).forEach(([key, value]) => {
              channelName = channelName.replace(`{${key}}`, value);
            });
          }

          // Build payload
          const payload: Record<string, any> = {
            event: broadcast.event,
            timestamp: Date.now(),
          };

          if (broadcast.getPayload) {
            Object.assign(payload, broadcast.getPayload(data, variables));
          }

          // Send broadcast
          const channel = supabase.channel(channelName);
          await channel.send({
            type: 'broadcast',
            event: broadcast.event,
            payload,
          });

          // Clean up channel after sending
          supabase.removeChannel(channel);

          if (process.env.NODE_ENV === 'development') {
            console.log(`[Broadcast] Sent ${broadcast.event} to ${channelName}`, payload);
          }
        } catch (error) {
          // Don't fail the mutation if broadcast fails
          console.error('[Broadcast] Failed to send:', error);
        }
      }

      // 3. Call original onSuccess handler
      if (originalOnSuccess) {
        await originalOnSuccess(data, variables, context);
      }
    },
  });
}

/**
 * Helper to create a broadcast subscription for receiving cross-device updates
 *
 * @example
 * useEffect(() => {
 *   const cleanup = subscribeToBroadcast('calendar-group:123', 'group_updated', () => {
 *     queryClient.invalidateQueries({ queryKey: ['calendar-group', '123'] });
 *   });
 *   return cleanup;
 * }, []);
 */
export function subscribeToBroadcast(
  channelName: string,
  event: string,
  onReceive: (payload: any) => void
): () => void {
  const channel = supabase
    .channel(channelName)
    .on('broadcast', { event }, (message) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Broadcast] Received ${event} on ${channelName}`, message.payload);
      }
      onReceive(message.payload);
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Hook to subscribe to multiple broadcast channels
 * Automatically cleans up on unmount
 *
 * @example
 * useBroadcastSubscription(
 *   groupIds.map(id => ({
 *     channel: `calendar-group:${id}`,
 *     event: 'group_change',
 *     onReceive: (payload) => {
 *       queryClient.invalidateQueries({ queryKey: ['calendar-group', id] });
 *     }
 *   })),
 *   [groupIds.join(',')]
 * );
 */
export function useBroadcastSubscription(
  subscriptions: Array<{
    channel: string;
    event: string;
    onReceive: (payload: any) => void;
  }>,
  deps: React.DependencyList = []
) {
  useEffect(() => {
    if (subscriptions.length === 0) return;

    const cleanups = subscriptions.map(({ channel, event, onReceive }) =>
      subscribeToBroadcast(channel, event, onReceive)
    );

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, deps);
}
